import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Table2 } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

const DATASETS = [
  { key: "allotment", label: "Allotment Data", filters: ["counselling_body", "year", "round", "state", "category", "quota"] },
  { key: "closing_ranks", label: "Closing Ranks", filters: ["counselling_body", "year", "round", "state", "category", "quota"] },
  { key: "users", label: "Users", filters: ["onboarding", "state", "plan", "role", "date_range"] },
  { key: "payments", label: "Payments", filters: ["status", "method", "date_range"] },
  { key: "cms", label: "CMS content", filters: ["content_type"] },
];

const PLAN_OPTIONS = ["", "free", "master"];
const ROLE_OPTIONS = ["", "user", "admin"];
const ONBOARDING_OPTIONS = ["", "complete", "incomplete"];
const PAYMENT_STATUSES = ["", "created", "paid", "failed", "amount_mismatch"];
const CMS_KINDS = ["", "events", "notices", "videos", "webinars"];

export default function AdminExport() {
  const [dataset, setDataset] = useState("allotment");
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [count, setCount] = useState(null);
  const [countBusy, setCountBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [format, setFormat] = useState("csv");

  const current = useMemo(() => DATASETS.find((d) => d.key === dataset), [dataset]);

  useEffect(() => {
    // Preload shared allotment / closing-rank filter options once.
    api
      .get("/filters")
      .then(({ data }) => setFilterOptions(data))
      .catch(() => {});
  }, []);

  const refreshCount = async (nextDataset = dataset, nextFilters = filters) => {
    setCountBusy(true);
    try {
      const params = { dataset: nextDataset, ...stripEmpty(nextFilters) };
      const { data } = await api.get("/admin/exports/count", { params });
      setCount(data);
    } catch (e) {
      toast.error(errMsg(e));
      setCount(null);
    } finally {
      setCountBusy(false);
    }
  };

  useEffect(() => {
    refreshCount(dataset, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);

  const stripEmpty = (obj) =>
    Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== "" && v != null));

  const switchDataset = (key) => {
    setDataset(key);
    setFilters({});
    setCount(null);
    if (key === "cms" && format === "xlsx") setFormat("xlsx");
  };

  const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const applyFilters = () => refreshCount(dataset, filters);

  const download = async () => {
    setDownloading(true);
    try {
      const params = { dataset, format, ...stripEmpty(filters) };
      const { data, headers } = await api.get("/admin/exports/download", {
        params,
        responseType: "blob",
      });
      const disposition = headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match
        ? match[1]
        : `neetone_${dataset}.${dataset === "cms" && format === "csv" ? "zip" : format}`;
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-export-page">
      <PageHead
        title="Export data"
        subtitle="Download any dataset as CSV or Excel. Filters match each dataset's native view."
      />

      <div className="grid gap-5 lg:grid-cols-[220px,1fr]">
        <aside
          className="border border-slate-200 bg-white"
          data-testid="dataset-picker"
        >
          <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            Dataset
          </p>
          <ul className="divide-y divide-slate-100">
            {DATASETS.map((d) => (
              <li key={d.key}>
                <button
                  data-testid={`dataset-${d.key}`}
                  onClick={() => switchDataset(d.key)}
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm ${
                    dataset === d.key
                      ? "bg-[#1A365D] text-white"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Table2 className="h-4 w-4" />
                  {d.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="space-y-4">
          <div
            className="border border-slate-200 bg-white p-4"
            data-testid="export-filters"
          >
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Filters
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {current?.filters.includes("counselling_body") && (
                <FilterSelect
                  label="Counselling body"
                  testid="filter-counselling_body"
                  value={filters.counselling_body || ""}
                  onChange={(v) => setFilter("counselling_body", v)}
                  options={["", ...(filterOptions.counselling_bodies || [])]}
                />
              )}
              {current?.filters.includes("year") && (
                <FilterSelect
                  label="Year"
                  testid="filter-year"
                  value={filters.year || ""}
                  onChange={(v) => setFilter("year", v)}
                  options={["", ...(filterOptions.years || [])]}
                />
              )}
              {current?.filters.includes("round") && (
                <FilterSelect
                  label="Round"
                  testid="filter-round"
                  value={filters.round || ""}
                  onChange={(v) => setFilter("round", v)}
                  options={["", ...(filterOptions.rounds || [])]}
                />
              )}
              {current?.filters.includes("state") && (
                <FilterSelect
                  label="State"
                  testid="filter-state"
                  value={filters.state || ""}
                  onChange={(v) => setFilter("state", v)}
                  options={["", ...(filterOptions.states || [])]}
                />
              )}
              {current?.filters.includes("category") && (
                <FilterSelect
                  label="Category (raw code)"
                  testid="filter-category"
                  value={filters.category || ""}
                  onChange={(v) => setFilter("category", v)}
                  options={["", ...(filterOptions.category_codes || [])]}
                />
              )}
              {current?.filters.includes("quota") && (
                <FilterSelect
                  label="Quota"
                  testid="filter-quota"
                  value={filters.quota || ""}
                  onChange={(v) => setFilter("quota", v)}
                  options={["", ...(filterOptions.quotas || [])]}
                />
              )}
              {current?.filters.includes("plan") && (
                <FilterSelect
                  label="Plan"
                  testid="filter-plan"
                  value={filters.plan || ""}
                  onChange={(v) => setFilter("plan", v)}
                  options={PLAN_OPTIONS}
                />
              )}
              {current?.filters.includes("role") && (
                <FilterSelect
                  label="Role"
                  testid="filter-role"
                  value={filters.role || ""}
                  onChange={(v) => setFilter("role", v)}
                  options={ROLE_OPTIONS}
                />
              )}
              {current?.filters.includes("onboarding") && (
                <FilterSelect
                  label="Onboarding"
                  testid="filter-onboarding"
                  value={filters.onboarding || ""}
                  onChange={(v) => setFilter("onboarding", v)}
                  options={ONBOARDING_OPTIONS}
                />
              )}
              {current?.filters.includes("status") && (
                <FilterSelect
                  label="Status"
                  testid="filter-status"
                  value={filters.status || ""}
                  onChange={(v) => setFilter("status", v)}
                  options={PAYMENT_STATUSES}
                />
              )}
              {current?.filters.includes("method") && (
                <FilterText
                  label="Payment method"
                  testid="filter-method"
                  value={filters.method || ""}
                  onChange={(v) => setFilter("method", v)}
                />
              )}
              {current?.filters.includes("date_range") && (
                <>
                  <FilterText
                    label="From (YYYY-MM-DD)"
                    testid="filter-date_from"
                    type="date"
                    value={filters.date_from || ""}
                    onChange={(v) => setFilter("date_from", v)}
                  />
                  <FilterText
                    label="To (YYYY-MM-DD)"
                    testid="filter-date_to"
                    type="date"
                    value={filters.date_to || ""}
                    onChange={(v) => setFilter("date_to", v)}
                  />
                </>
              )}
              {current?.filters.includes("content_type") && (
                <FilterSelect
                  label="Content type"
                  testid="filter-content_type"
                  value={filters.content_type || ""}
                  onChange={(v) => setFilter("content_type", v)}
                  options={CMS_KINDS}
                />
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid="apply-filters-btn"
                onClick={applyFilters}
                className="border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-700 hover:border-[#1A365D]"
              >
                Preview count
              </button>
              <button
                data-testid="reset-filters-btn"
                onClick={() => {
                  setFilters({});
                  refreshCount(dataset, {});
                }}
                className="text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                Reset
              </button>
            </div>
          </div>

          <div
            className="flex flex-wrap items-center gap-4 border border-slate-200 bg-white p-4"
            data-testid="export-actions"
          >
            <div className="text-sm">
              <span className="font-mono-num text-base font-semibold text-slate-900" data-testid="row-count">
                {countBusy ? "…" : count ? (count.approximate ? "~" : "") + count.count.toLocaleString("en-IN") : "0"}
              </span>{" "}
              <span className="text-slate-500">rows will be exported</span>
            </div>
            <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
              Format
              <select
                data-testid="format-select"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="h-9 border border-slate-300 px-2 text-sm"
              >
                <option value="csv">CSV{dataset === "cms" ? " (zip)" : ""}</option>
                <option value="xlsx">Excel (.xlsx)</option>
              </select>
            </label>
            <button
              data-testid="download-export-btn"
              onClick={download}
              disabled={downloading || !count || count.count === 0}
              className="flex h-9 items-center gap-2 bg-[#1A365D] px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845] disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              {downloading ? "Preparing…" : "Export"}
            </button>
          </div>

          {dataset === "cms" && (
            <p className="text-xs text-slate-500" data-testid="cms-hint">
              Excel exports group every CMS content type (Events, Notices, Videos, Webinars) as separate
              sheets in a single workbook. CSV exports deliver one zipped bundle of per-type CSVs.
            </p>
          )}
          {dataset === "users" && (
            <p className="text-xs text-slate-500" data-testid="users-hint">
              Sensitive columns (passwords, session tokens, OTP hashes) are never included.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

const FilterSelect = ({ label, testid, value, onChange, options }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 border border-slate-300 px-2 text-sm"
    >
      {options.map((o) => (
        <option key={String(o)} value={o}>
          {o === "" ? "All" : String(o)}
        </option>
      ))}
    </select>
  </label>
);

const FilterText = ({ label, testid, value, onChange, type = "text" }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 border border-slate-300 px-2 font-mono-num text-sm"
    />
  </label>
);
